import { FormEvent, useState } from "react";
import { AlertTriangle, CalendarDays } from "lucide-react";
import { Modal } from "../Modal";
import { formatSchoolDate } from "../students/studentDisplay";
import { apiFieldErrors } from "../../utils/apiError";
import type { LeaveRequest, LeaveType, StaffProfile } from "../../services/staffService";
import { leaveBalance, leaveDays, localToday, overlappingLeave } from "../../pages/leaveFilters";

export type LeaveRequestValues = {
  staff?: number;
  leave_type: number;
  start_date: string;
  end_date: string;
  reason: string;
};

const FORM_ID = "leave-request-form";

export default function LeaveRequestModal({
  canManage,
  profiles,
  leaveTypes,
  leaves,
  ownProfileId,
  isSaving,
  error,
  onSubmit,
  onClose,
}: {
  canManage: boolean;
  profiles: StaffProfile[];
  leaveTypes: LeaveType[];
  leaves: LeaveRequest[];
  ownProfileId?: number;
  isSaving: boolean;
  error: unknown;
  onSubmit: (values: LeaveRequestValues) => void;
  onClose: () => void;
}) {
  const [staff, setStaff] = useState("");
  const [typeId, setTypeId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const { fields: errors, message } = apiFieldErrors(
    error,
    ["leave_type", "start_date", "end_date", "reason"],
    "The leave request could not be submitted. Please try again.",
  );

  const staffId = Number(staff) || ownProfileId;
  const leaveType = leaveTypes.find((type) => String(type.id) === typeId);
  const days = leaveDays(start, end);
  const balance = leaveBalance(leaves, staffId, leaveType, Number((start || localToday()).slice(0, 4)));
  const leftAfter = balance ? balance.left - days : null;
  const clashes = overlappingLeave(leaves, staffId, start, end);
  const activeTypes = leaveTypes.filter((type) => type.is_active);
  const bookable = profiles.filter((profile) =>
    ["active", "probation", "suspended"].includes(profile.employment_status),
  );

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit({
      staff: Number(staff) || undefined,
      leave_type: Number(typeId),
      start_date: start,
      end_date: end,
      reason: reason.trim(),
    });
  };

  const fieldError = (name: string) =>
    errors[name] && (
      <span role="alert" className="mt-1 block text-xs font-normal text-red-700">
        {errors[name]}
      </span>
    );

  return (
    <Modal
      isOpen
      size="2xl"
      title="Request leave"
      onClose={() => {
        if (!isSaving) onClose();
      }}
      footer={
        <>
          <button type="button" className="btn btn-outline" disabled={isSaving} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form={FORM_ID}
            disabled={isSaving || clashes.length > 0}
            className="btn btn-primary"
          >
            {isSaving ? "Submitting…" : "Submit request"}
          </button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit} className="font-refined space-y-5">
        {message && (
          <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            {message}
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {canManage && (
            <label className="block text-sm font-medium text-gray-700">
              For
              <select value={staff} onChange={(event) => setStaff(event.target.value)} className="input mt-1">
                <option value="">Myself</option>
                {bookable.map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.user_name} · {profile.position_title}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className={`block text-sm font-medium text-gray-700 ${canManage ? "" : "sm:col-span-2"}`}>
            Leave type *
            <select
              required
              value={typeId}
              onChange={(event) => setTypeId(event.target.value)}
              className="input mt-1"
            >
              <option value="">Choose a leave type</option>
              {activeTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} · {type.max_days_per_year} days a year{type.is_paid ? "" : " · unpaid"}
                </option>
              ))}
            </select>
            {fieldError("leave_type")}
          </label>
          <label className="block text-sm font-medium text-gray-700">
            First day *
            <input
              required
              type="date"
              value={start}
              onChange={(event) => {
                setStart(event.target.value);
                if (!end || end < event.target.value) setEnd(event.target.value);
              }}
              className="input mt-1"
            />
            {fieldError("start_date")}
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Last day *
            <input
              required
              type="date"
              value={end}
              min={start || undefined}
              onChange={(event) => setEnd(event.target.value)}
              className="input mt-1"
            />
            {fieldError("end_date")}
          </label>
        </div>

        {days > 0 && (
          <div className="overflow-hidden rounded-lg border border-gray-200 text-sm">
            <div className="flex flex-wrap items-center gap-2 bg-gray-50 px-4 py-2.5">
              <CalendarDays className="h-4 w-4 text-gray-400" />
              <span className="font-semibold text-gray-900">
                {days} day{days === 1 ? "" : "s"}
              </span>
              <span className="text-gray-500">
                {formatSchoolDate(start)}
                {end !== start && ` to ${formatSchoolDate(end)}`} · calendar days, weekends included
              </span>
            </div>
            {balance && leaveType && (
              <dl className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200 text-center sm:grid-cols-4">
                {[
                  ["Allowed", balance.allowed, "text-gray-900"],
                  ["Taken", balance.used, "text-gray-900"],
                  ["Pending", balance.pending, "text-amber-700"],
                  [
                    "Left after this",
                    leftAfter,
                    leftAfter !== null && leftAfter < 0 ? "text-red-700" : "text-green-700",
                  ],
                ].map(([label, value, tone]) => (
                  <div key={label as string} className="px-3 py-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
                    <dd className={`text-base font-semibold tabular-nums ${tone}`}>{value}</dd>
                  </div>
                ))}
              </dl>
            )}
            {leftAfter !== null && leftAfter < 0 && (
              <p className="border-t border-amber-200 bg-amber-50 px-4 py-2 text-amber-900">
                This goes {Math.abs(leftAfter)} day{leftAfter === -1 ? "" : "s"} over the yearly {leaveType?.name}{" "}
                allowance. It can still be requested; the approver will see it.
              </p>
            )}
          </div>
        )}

        {clashes.length > 0 && (
          <div role="alert" className="flex gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              These dates overlap{" "}
              {clashes
                .map(
                  (leave) =>
                    `${leave.status} ${leave.leave_type_name} (${formatSchoolDate(leave.start_date)} to ${formatSchoolDate(leave.end_date)})`,
                )
                .join(", ")}
              . Choose other days, or cancel that request first.
            </p>
          </div>
        )}

        <label className="block text-sm font-medium text-gray-700">
          Reason *
          <textarea
            required
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="input mt-1 h-auto"
            placeholder="e.g. Family wedding upcountry. Lessons covered by Mr Okello."
          />
          {fieldError("reason")}
        </label>
      </form>
    </Modal>
  );
}
